<template>
  <v-container>
    <div v-if="!claimed">
      <TaskBlocker :assignee="data.assignee" class="mb-4 px-4" />
    </div>

    <div v-if="taskDataLoading" class="ma-9">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      Task Daten werden geladen
    </div>
    <div v-else>
      <!-- Project details, technology selector and global hostname button -->
      <v-row>
        <v-col cols="12" md="7">
          <v-expansion-panels :value="false">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span>
                  <span>Projekt Details </span>
                  <span v-if="variables && variables.projectId">{{
                    variables.projectId
                  }}</span>
                  <span v-if="project && project.name">
                    - {{ project.name }}
                  </span>
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ProjectDetails
                  :project="project"
                  hide-clever-reach-button
                  hide-brevo-button
                  hide-tki-button
                  hide-statistic
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Status labels -->
      <v-row>
        <v-col cols="12">
          <div class="v-item-group theme--dark v-slide-group v-chip-group">
            <div class="v-slide-group__wrapper">
              <v-chip color="success" label>
                {{ countSuccess }} Erfolgreich
                <v-icon right>
                  {{ icons.mdiCheckCircleOutline }}
                </v-icon>
              </v-chip>
              <v-chip color="secondary" label>
                {{ countPending }} Pending
                <v-icon right>
                  {{ icons.mdiTimerSand }}
                </v-icon>
              </v-chip>
              <v-chip color="warning" label>
                {{ countWarning }} Fehlerhaft
                <v-icon right>
                  {{ icons.mdiAlertCircleOutline }}
                </v-icon>
              </v-chip>
              <v-chip color="error" label>
                {{ incidents.length }} Incident
                <v-icon right>
                  {{ icons.mdiCancel }}
                </v-icon>
              </v-chip>
              <v-chip color="primary" label>
                {{ resourceImportList.length }} Anzahl Importe
                <v-icon right>
                  {{ icons.mdiCounter }}
                </v-icon>
              </v-chip>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Incidents -->
      <v-row v-if="incidents.length > 0">
        <v-col cols="12">
          <v-alert color="error" outlined>
            Während des Imports sind
            <abbr title="Fehler bei der Ausführung in der Prozess Engine">
              Incidents
            </abbr>
            aufgetreten. Der Import kann erst abgeschlossen werden, wenn alle
            Incidents behoben wurden.

            <v-dialog v-model="showIncidentDialog" width="800" scrollable>
              <template #activator="{ on, attrs }">
                <v-btn
                  color="error"
                  small
                  text
                  outlined
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>
                    {{ icons.mdiUpload }}
                  </v-icon>
                  Incidents anzeigen
                </v-btn>
              </template>
              <v-card style="height: 75vh" :loading="isRetryRunning">
                <v-card-title>{{ incidents.length }} Incidents</v-card-title>
                <v-card-text class="custom-scrollbar">
                  <v-list two-line dense>
                    <template v-for="(incident, index) of incidents">
                      <v-list-item :key="`${index}item`">
                        <v-list-item-content :key="`${index}content`">
                          <v-list-item-title>
                            {{ incident.failedActivityId }}
                          </v-list-item-title>

                          <v-list-item-subtitle class="text--primary">
                            {{ incident.incidentMessage }}
                          </v-list-item-subtitle>

                          <v-list-item-subtitle>
                            {{ formatIncidentDate(incident.incidentTimestamp) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action :key="`${index}action`">
                          <v-list-item-action-text>
                            Retry
                          </v-list-item-action-text>

                          <v-btn
                            :disabled="isRetryRunning"
                            @click="retryIncident(incident)"
                            icon
                          >
                            <v-icon>
                              {{ icons.mdiReload }}
                            </v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </template>
                  </v-list>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click="showIncidentDialog = false" text>
                    Schließen
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Table -->
      <v-row>
        <v-col cols="12" md="12">
          <v-data-table
            :headers="tableHeaders"
            :items="tableItems"
            :loading="isSubmitting"
            :search="tableSearchTerm"
            :items-per-page="tableItemsPerPage"
            sort-by="contractId"
            item-key="contractId"
            class="custom-scrollbar"
            @update:items-per-page="updateLocalStorage('itemsPerPage', $event)"
          >
            <template #top>
              <v-toolbar flat>
                <div
                  class="py-2 pr-2 d-flex justify-space-between align-center"
                  style="width: 100%"
                >
                  <v-switch
                    v-model="showTableDetails"
                    class="mt-6"
                    style="display: flex; justify-content: center"
                    label="Alle Felder anzeigen"
                  ></v-switch>
                  <v-switch
                    v-model="showInvalidOnly"
                    class="mt-6 px-4"
                    style="display: flex; justify-content: center"
                    label="Nur Import Fehler anzeigen"
                  ></v-switch>
                  <v-text-field
                    v-model="tableSearchTerm"
                    placeholder="Suche"
                    class="mt-5 px-9"
                  />
                  <v-btn small icon @click="downloadCurrentTable">
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </div>
              </v-toolbar>
            </template>
            <template #[`item.state`]="{ item }">
              <v-icon :color="stateColor(item)" class="mr-2">
                {{ stateIcon(item) }}
              </v-icon>
            </template>
            <template #[`item.ontResourceId`]="{ item }">
              <v-icon
                :color="item.ontResourceId ? 'success' : 'error'"
                class="mr-2"
              >
                {{
                  item.ontResourceId
                    ? icons.mdiCheckCircleOutline
                    : icons.mdiCancel
                }}
              </v-icon>
              <sup>{{ item.ontResourceId }}</sup>
            </template>
            <template #[`item.uniPortResourceId`]="{ item }">
              <v-icon
                :color="item.uniPortResourceId ? 'success' : 'error'"
                class="mr-2"
              >
                {{
                  item.uniPortResourceId
                    ? icons.mdiCheckCircleOutline
                    : icons.mdiCancel
                }}
              </v-icon>
              <sup>{{ item.uniPortResourceId }}</sup>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Import button -->
      <v-row>
        <v-col cols="12" md="12">
          <div class="mt-6 align-buttons-right">
            <v-btn
              :disabled="!isImportFinished"
              :loading="false"
              color="primary"
              @click="showSubmitDialog = true"
            >
              Aufgabe schließen
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>

    <v-dialog v-model="showSubmitDialog" :persistent="isSubmitting" width="600">
      <v-card v-if="project" :loading="isSubmitting">
        <v-card-title> Aufgabe schließen </v-card-title>

        <v-card-text>
          <span class="v-label"> Soll die Aufgabe geschlossen werden? </span>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            :disabled="isSubmitting"
            color="secondary"
            @click="showSubmitDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :loading="isSubmitting"
            :disabled="!isImportFinished || showInvalidOnly"
            color="primary"
            @click="submitTask"
          >
            Ja, Aufgabe schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { HTTP } from '@/main/httpClient.js';
import { adminGroups } from '@/main/routes.js';
import {
  convertObjectArrayToCsv,
  downloadStringAsFile
} from '@/main/helper.js';
import {
  mdiCounter,
  mdiCheckCircleOutline,
  mdiAlertCircleOutline,
  mdiCancel,
  mdiTimerSand,
  mdiReload
} from '@mdi/js';

import TaskBlocker from '@/task/TaskBlocker.vue';
import NotificationObject from '@/main/NotificationObject.js';

const PAST_ERROR_MESSAGE = 'PAST Daten konnten nicht abgerufen werden';
const TASK_ACTIVITY_ID = 'RessourcenImportErgebnisTask';
const SETTING_KEY = 'resourceImport';

export default {
  name: 'ActionRessourcenImportErgebnisTask',
  components: {
    TaskBlocker,
    ProjectDetails: () =>
      import('@/components/constructionProjects/ProjectDetails.vue')
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    icons: {
      mdiCounter,
      mdiCheckCircleOutline,
      mdiAlertCircleOutline,
      mdiCancel,
      mdiTimerSand,
      mdiReload
    },
    variables: null,
    taskDataLoading: false,
    project: null,
    showSubmitDialog: false,
    showIncidentDialog: false,
    isSubmitting: false,
    isSaving: false,
    resourceImportList: [],
    showTableDetails: false,
    baseTableHeaders: [
      { text: 'AGS', value: 'ags', isDetail: true },
      {
        text: 'Vertragsnummer',
        value: 'contractId',
        isDetail: false,
        width: '150px'
      },
      { text: 'Home ID', value: 'homeId', isDetail: true },
      { text: 'ONT Seriennummer', value: 'ontSerialNumber', isDetail: true },
      { text: 'MFG', value: 'mfg', isDetail: true },
      { text: 'MFG Splice Panel', value: 'mfgSplicePanel', isDetail: true },
      { text: 'MFG Panel Port', value: 'mfgPanelPort', isDetail: true },
      { text: 'MSAN Hostname', value: 'deviceHostname', isDetail: true },
      { text: 'MSAN Slot', value: 'deviceSlot', isDetail: true },
      { text: 'MSAN Port', value: 'devicePort', isDetail: true },
      {
        text: 'ONT Assigned',
        value: 'ontResourceId',
        isDetail: false,
        width: '130px'
      },
      {
        text: 'Port Assigned',
        value: 'uniPortResourceId',
        isDetail: false,
        width: '130px'
      },
      { text: 'Import Fehler', value: 'error', isDetail: false }
    ],
    tableItemsPerPage: 15,
    tableSearchTerm: '',
    showInvalidOnly: false,
    isRetryRunning: false,
    activeActivityIds: [],
    incidents: []
  }),
  computed: {
    username() {
      return this.$store.getters.getUsername;
    },
    claimed() {
      return this.data.assignee === this.username;
    },
    isAdmin() {
      return this.$auth
        .user()
        .userRoles.some((role) => adminGroups.includes(role));
    },
    tableHeaders() {
      return [
        {
          text: 'Status',
          value: 'state',
          isDetail: false,
          width: '70px',
          sortable: false
        },
        ...this.baseTableHeaders
          .filter(
            (h) => this.showTableDetails || h.isDetail === this.showTableDetails
          )
          .map((h) => {
            if (h.value === 'error') {
              h.width = this.showTableDetails ? '300px' : null;
            }
            return h;
          })
      ];
    },
    isImportFinished() {
      return this.activeActivityIds.length === 0 && this.incidents.length === 0;
    },
    tableItems() {
      if (this.taskDataLoading) return [];
      return this.showInvalidOnly
        ? this.resourceImportList.filter((e) => e.error)
        : this.resourceImportList;
    },
    countSuccess() {
      return this.resourceImportList?.filter(
        (i) => i.ontResourceId > 0 && i.uniPortResourceId > 0
      ).length;
    },
    countPending() {
      return this.activeActivityIds.length;
    },
    countWarning() {
      return this.resourceImportList?.filter((i) => i.error !== undefined)
        .length;
    }
  },
  mounted: async function () {
    this.taskDataLoading = true;
    // fix race condition after reopen the same task
    await new Promise((resolve) => {
      setTimeout(resolve, 1);
    });

    const settings = this.$settings.get(SETTING_KEY, {});
    this.tableItemsPerPage = settings?.itemsPerPage ?? 15;

    Promise.all([
      this.data.variables.then(this.handleVariables),
      this.fetchTaskData()
    ])
      .then(([, { importResults, incidents, activeActivityIds }]) => {
        this.activeActivityIds = activeActivityIds.filter(
          (i) => i !== TASK_ACTIVITY_ID
        );
        this.incidents = incidents;

        this.resourceImportList = importResults;
        this.taskDataLoading = false;
      })
      .catch((err) => {
        console.error(err);
        this.showMessage('error', [
          'Beim laden der Aufgabe ist ein Fehler aufgetreten',
          err
        ]);
      });
  },
  methods: {
    validate() {
      this.$refs.technologySelector.validate();
    },
    handleVariables: function (taskVariables) {
      this.variables = Object.keys(taskVariables).reduce((obj, key) => {
        obj[key] = taskVariables[key].value;
        return obj;
      }, {});

      return new Promise((resolve, reject) => {
        HTTP.get(
          '/past/projects?where[projektId][equals]=' + this.variables.projectId
        )
          .then(({ data, status }) => {
            if (status !== 200 || !data || data.length !== 1) {
              throw new Error(PAST_ERROR_MESSAGE);
            }
            this.project = data[0];
            return resolve(this.project);
          })
          .catch((err) => {
            console.error(err);
            return reject(PAST_ERROR_MESSAGE);
          });
      });
    },
    fetchTaskData: function () {
      return HTTP.get(`gncReport/resourceImport/${this.data.id}/report`)
        .then(({ data }) => {
          return data;
        })
        .catch((err) => {
          console.error(err);
          throw new Error('Der Import Report konnte nicht geladen werden');
        });
    },
    submitTask: function () {
      if (!this.isImportFinished || !this.claimed) return;
      this.isSubmitting = true;

      HTTP.post(`/engine/default/task/${this.data.id}/complete`, {})
        .then(({ data, status }) => {
          if (status !== 204) {
            throw new Error(data);
          }

          this.showMessage(
            'success',
            'Die Aufgabe wurde erfolgreich geschlossen'
          );

          this.$store.commit('triggerTasksRefresh');
          this.$emit('onClose');
        })
        .catch((err) => {
          console.log(err);
          this.showMessage('error', [
            'Die Aufgabe konnte nicht geschlossen werden',
            err
          ]);
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    retryIncident: function (incident) {
      this.isRetryRunning = true;
      HTTP.put(`engine/default/job/${incident.configuration}/retries`, {
        retries: 1
      })
        .then(() => {
          this.incidents = this.incidents.filter((i) => i.id !== incident.id);
        })
        .finally(() => {
          this.isRetryRunning = false;
        });
    },
    downloadCurrentTable: function () {
      const filename =
        `RessourcenImportErgebnis_${this.variables.projectId}_${
          this.project.name
        }_${new Date().toISOString().slice(0, 10)}`
          .replace(/[^a-z0-9]/gi, '_')
          .replace(/_+/gi, '_') + '.csv';

      downloadStringAsFile(
        convertObjectArrayToCsv(this.tableItems),
        filename,
        'text/csv;charset=utf-8;'
      );
    },
    downloadStringAsFile: downloadStringAsFile,
    formatIncidentDate: function (date) {
      return new Date(Date.parse(date)).toLocaleString('de-DE');
    },
    showMessage: function (type, description) {
      this.$store.commit(
        'addNotification',
        new NotificationObject(type, description)
      );
    },
    updateLocalStorage: function (key, value) {
      this.$settings.set(`${SETTING_KEY}.${key}`, value);
    },
    stateColor(item) {
      if (item.error?.length > 0) {
        return 'warning';
      }

      if (item.ontResourceId && item.uniPortResourceId) {
        return 'success';
      }

      return 'secondary';
    },
    stateIcon(item) {
      if (item.error?.length > 0) {
        return this.icons.mdiAlertCircleOutline;
      }

      if (item.ontResourceId && item.uniPortResourceId) {
        return this.icons.mdiCheckCircleOutline;
      }

      return this.icons.mdiTimerSand;
    }
  }
};
</script>
